import { NgClass } from '@angular/common';
import { Component, computed, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputErrorComponent } from '@common/components/input/input-error/input-error.component';
import { InputLabelComponent } from '@common/components/input/input-label/input-label.component';
import { Setting } from '@common/interfaces/setting.interface';
import { SettingKeyCodes } from '@common/known-types/setting-key.codes';
import { CustomDatePipe } from '@common/pipes/date.pipe';
import { environment } from '@environments/environment.base';
import {
    DateInputComponent,
    DateRangeComponent,
    DateRangeEndInputDirective,
    DateRangeStartInputDirective
} from '@progress/kendo-angular-dateinputs';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { BaseInputComponent } from '../base-input/base-input.component';
import { DateInput } from '../date-input/date-input.component';
import { DateRangeInputConfig } from '../input.type';

/**
 * Daterange input component.
 * @param format - The format of the date.
 * @param minDate - The minimum date.
 * @param maxDate - The maximum date.
 */
@Component({
    selector: 'app-daterange-input',
    templateUrl: './daterange-input.component.html',
    providers: [{ provide: BaseInputComponent, useExisting: DaterangeInputComponent }],
    imports: [
        InputLabelComponent,
        DateRangeComponent,
        DateInputComponent,
        DateRangeStartInputDirective,
        NgClass,
        FormsModule,
        DateRangeEndInputDirective,
        InputErrorComponent,
        TooltipDirective,
        CustomDatePipe
    ]
})
export class DaterangeInputComponent extends BaseInputComponent<{ startDate: DateInput; endDate: DateInput }> {
    @Input() format: DateRangeInputConfig['format'];
    @Input() formatPlaceholder: DateRangeInputConfig['formatPlaceholder'] = {
        day: this.translateService.instant('day'),
        month: this.translateService.instant('month'),
        year: this.translateService.instant('year'),
        hour: this.translateService.instant('hour'),
        minute: this.translateService.instant('minute'),
        second: this.translateService.instant('second'),
        millisecond: this.translateService.instant('millisecond')
    };
    @Input() minDate: DateRangeInputConfig['minDate'];
    @Input() maxDate: DateRangeInputConfig['maxDate'];

    defaultDateFormat: string = environment.settings.appControl.dateFormat;

    constructor() {
        super();
        this.getDefaultDateFormat();
    }

    dateValue = computed<{ startDate: Date; endDate: Date }>(() => {
        if (!this.value()) return null;

        const startDateInput = this.value().startDate;
        const endDateInput = this.value().endDate;

        if (startDateInput instanceof Date && endDateInput instanceof Date) {
            return this.value() as { startDate: Date; endDate: Date };
        }

        if (startDateInput instanceof Date && typeof endDateInput === 'string') {
            const endDate = new Date(endDateInput);
            return isNaN(endDate.getTime()) ? null : { startDate: startDateInput as Date, endDate };
        }

        if (typeof startDateInput === 'string' && endDateInput instanceof Date) {
            const startDate = new Date(startDateInput);
            return isNaN(startDate.getTime()) ? null : { startDate, endDate: endDateInput as Date };
        }

        if (typeof startDateInput === 'string' && typeof endDateInput === 'string') {
            const startDate = new Date(startDateInput);
            const endDate = new Date(endDateInput);
            return isNaN(startDate.getTime()) || isNaN(endDate.getTime()) ? null : { startDate, endDate };
        }

        return null;
    });

    override onValueChange(value: { startDate: Date; endDate: Date }) {
        this.validate(value);
        this.dirty.set(true);
        this.valueChange.emit(value);
        this.value.set(value);
    }

    override validate(value: { startDate: Date; endDate: Date }) {
        const error: string[] = [];

        if (this.validateFn) {
            const validateResult = this.validateFn(value);
            if (validateResult) {
                error.push(validateResult);
            }
        }

        if (this.isRequired && (!value?.startDate || !value?.endDate)) {
            error.push(this.translateService.instant('This field is required'));
        }

        this.error.set(error.length > 0 ? error : null);
    }

    private getDefaultDateFormat() {
        const settings = this.queryService.queryClient.getQueryData<Setting[]>(['settings']);
        if (!settings) return;
        const defaultDateFormat = settings.find(
            (setting: Setting) => setting.key === SettingKeyCodes.DateFormat
        )?.value;
        if (defaultDateFormat) this.format = this.defaultDateFormat = defaultDateFormat;
    }
}
